import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconCash, IconCheck, IconX, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import { GrHoverTooltip } from './GestaoReembolsosTooltip';

export function canApprove(statusKey) {
  return statusKey === 'pendente' || statusKey === 'revisado';
}

export function canReject(statusKey) {
  return statusKey === 'pendente' || statusKey === 'revisado';
}

export function canPay(statusKey) {
  return statusKey === 'aprovado';
}

export function GrExpenseCheckbox({ checked, onChange, title, indeterminate = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  const control = (
    <label
      className="gr-check-label"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={ref}
        type="checkbox"
        className="gr-check-native"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        aria-label={title || 'Selecionar'}
      />
      <span className="gr-check-box" aria-hidden />
    </label>
  );

  return control;
}

function RejectMenu({ open, onClose, onDefinitive, onReview, anchorRef }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current) {
      setPos(null);
      return undefined;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: Math.min(rect.right - 160, window.innerWidth - 172),
    });
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="gr-reject-menu"
      role="menu"
      style={{ top: pos.top, left: pos.left }}
    >
      <button
        type="button"
        className="gr-reject-menu-item"
        role="menuitem"
        onClick={() => {
          onDefinitive?.();
          onClose?.();
        }}
      >
        Definitivo
      </button>
      <button
        type="button"
        className="gr-reject-menu-item"
        role="menuitem"
        onClick={() => {
          onReview?.();
          onClose?.();
        }}
      >
        Revisar
      </button>
    </div>,
    document.body
  );
}

export function GestaoReembolsosReviewNoteModal({
  open,
  onClose,
  onConfirm,
  loading,
  mode = 'review',
}) {
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const maxLen = 300;
  const isDefinitive = mode === 'definitive';

  useEffect(() => {
    if (open) {
      setNote('');
      setErr('');
    }
  }, [open, mode]);

  if (!open) return null;

  const submit = () => {
    const t = note.trim();
    if (!t) {
      setErr(isDefinitive ? 'Informe o motivo da rejeição definitiva.' : 'Informe as observações para revisar.');
      return;
    }
    if (t.length > maxLen) {
      setErr(`Máximo de ${maxLen} caracteres.`);
      return;
    }
    onConfirm?.(t);
  };

  const remaining = maxLen - note.length;

  return ReactDOM.createPortal(
    <div
      className="gr-review-modal"
      role="dialog"
      aria-modal="true"
      aria-label={isDefinitive ? 'Motivo da rejeição' : 'Observações de revisão'}
    >
      <div className="gr-review-modal-backdrop" onClick={() => !loading && onClose?.()} />
      <div className="gr-review-modal-panel">
        <h3 className="gr-review-modal-title">
          {isDefinitive ? 'Motivo da rejeição definitiva' : 'Observações de revisão'}
        </h3>
        <p className="gr-review-modal-hint">
          {isDefinitive
            ? 'Obrigatório. O lançamento será marcado como negado.'
            : 'Obrigatório. O lançamento será marcado como rejeitado para ajuste.'}
        </p>
        <textarea
          className="gr-review-modal-textarea"
          value={note}
          onChange={(e) => {
            const v = e.target.value.slice(0, maxLen);
            setNote(v);
            if (err) setErr('');
          }}
          rows={4}
          maxLength={maxLen}
          placeholder={isDefinitive ? 'Descreva o motivo da rejeição…' : 'Descreva o ajuste necessário…'}
          disabled={loading}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <div className="gr-review-modal-count" aria-live="polite">
          {remaining} restantes
        </div>
        {err ? (
          <div className="gr-review-modal-error" role="alert">
            {err}
          </div>
        ) : null}
        <div className="gr-review-modal-actions">
          <button type="button" className="gr-action-btn" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="button"
            className="gr-action-btn is-danger"
            onClick={submit}
            disabled={loading}
          >
            {loading ? 'Salvando…' : isDefinitive ? 'Confirmar rejeição' : 'Confirmar revisão'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * @param {{ showApprove?: boolean, showReject?: boolean, showPay?: boolean }} visibility
 * Se visibility omitido, deriva do statusKey.
 */
export function ExpenseStatusActions({
  statusKey,
  showApprove: showApproveProp,
  showReject: showRejectProp,
  showPay: showPayProp,
  onApprove,
  onPay,
  onRejectDefinitive,
  onRejectReview,
  disabled = false,
  busy = false,
}) {
  const rejectRef = useRef(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const showApprove = showApproveProp ?? canApprove(statusKey);
  const showReject = showRejectProp ?? canReject(statusKey);
  const showPay = showPayProp ?? canPay(statusKey);

  if (busy) {
    return (
      <div className="gr-actions-cell is-busy" aria-busy="true">
        <span className="gr-action-busy-spin" aria-hidden />
        <span className="gr-action-busy-label">Processando</span>
      </div>
    );
  }

  if (!showApprove && !showReject && !showPay) {
    return <span className="gr-actions-empty">—</span>;
  }

  return (
    <div className={`gr-actions-cell${disabled ? ' is-disabled' : ''}`}>
      {showApprove ? (
        <button
          type="button"
          className="gr-action-btn is-approve"
          onClick={onApprove}
          disabled={disabled}
        >
          <IconCheck size={13} stroke={2} aria-hidden />
          Aprovar
        </button>
      ) : null}
      {showReject ? (
        <>
          <button
            ref={rejectRef}
            type="button"
            className={`gr-action-btn is-reject${rejectOpen ? ' is-open' : ''}`}
            onClick={() => !disabled && setRejectOpen((v) => !v)}
            aria-expanded={rejectOpen}
            disabled={disabled}
          >
            <IconX size={13} stroke={2} aria-hidden />
            Rejeitar
          </button>
          <RejectMenu
            open={rejectOpen && !disabled}
            anchorRef={rejectRef}
            onClose={() => setRejectOpen(false)}
            onDefinitive={onRejectDefinitive}
            onReview={onRejectReview}
          />
        </>
      ) : null}
      {showPay ? (
        <button
          type="button"
          className="gr-action-btn is-pay"
          onClick={onPay}
          disabled={disabled}
        >
          <IconCash size={13} stroke={2} aria-hidden />
          Pagar
        </button>
      ) : null}
    </div>
  );
}

export function ObsCell({ text }) {
  const full = text || '—';
  const textRef = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const measure = () => {
      setTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [full]);

  return (
    <span className={`gr-detail-obs-wrap${truncated ? ' is-truncated' : ''}`} tabIndex={truncated ? 0 : -1}>
      <span ref={textRef} className="gr-detail-obs">
        {full}
      </span>
      {truncated && full && full !== '—' ? (
        <span className="gr-detail-obs-tip" role="tooltip">
          {full}
        </span>
      ) : null}
    </span>
  );
}

/** Modal de comprovante no padrão CheckoutView (fotos/assinatura). */
export function GestaoReembolsosReceiptModal({ open, onClose, url, meta }) {
  const [kind, setKind] = useState('image'); /* image | pdf | unknown */
  const [imgError, setImgError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open || !url) return;
    setImgError(false);
    setKind('image');
    setExpanded(false);
  }, [open, url]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (expanded) {
          setExpanded(false);
          return;
        }
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, expanded]);

  if (!open || !url) return null;

  const title = meta?.code ? `Comprovante · ${meta.code}` : 'Comprovante';
  const subtitle = [meta?.expenseTypeName, meta?.amountLabel ? `R$ ${meta.amountLabel}` : null]
    .filter(Boolean)
    .join(' · ') || 'Anexo do lançamento';

  const showAsPdf = kind === 'pdf' || (imgError && kind !== 'unknown');

  return ReactDOM.createPortal(
    <div className="gr-receipt-overlay modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="gr-receipt-backdrop" onClick={onClose} aria-hidden />
      <div
        className={`gr-receipt-modal${expanded ? ' is-expanded' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gr-receipt-header">
          <div className="gr-receipt-header-info">
            <h3 className="gr-receipt-title">{title}</h3>
            <div className="gr-receipt-subtitle">{subtitle}</div>
          </div>
          <button
            type="button"
            className="gr-receipt-close"
            onClick={onClose}
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        <div className={`gr-receipt-body${expanded ? ' is-expanded' : ''}`}>
          {!showAsPdf ? (
            <div className={`gr-receipt-frame${expanded ? ' is-expanded' : ''}`}>
              <GrHoverTooltip
                className="gr-receipt-zoom-tip"
                content={expanded ? 'Ajustar à tela' : 'Tamanho original'}
                delayMs={160}
              >
                <button
                  type="button"
                  className="gr-receipt-zoom-btn"
                  onClick={() => setExpanded((v) => !v)}
                  aria-label={expanded ? 'Ajustar à tela' : 'Tamanho original'}
                  aria-pressed={expanded}
                >
                  {expanded ? (
                    <IconZoomOut size={16} stroke={1.75} aria-hidden />
                  ) : (
                    <IconZoomIn size={16} stroke={1.75} aria-hidden />
                  )}
                </button>
              </GrHoverTooltip>
              <img
                src={url}
                alt="Comprovante"
                className={`gr-receipt-img${expanded ? ' is-native' : ''}`}
                onError={() => {
                  setImgError(true);
                  setKind('pdf');
                  setExpanded(false);
                }}
              />
            </div>
          ) : (
            <iframe
              title={title}
              src={url}
              className="gr-receipt-iframe"
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
